import React, { useState, useEffect, useRef } from 'react'
import { TABLE_COLUMNS, COLORS } from '../constants'
import {
  formatPosition,
  isRankingActive,
  generateRandomPercentage,
} from '../utils'
import SkeletonCard from './SkeletonCard'
import LiveRankingModal from '../../../components/Common/Modal/LiveRankingModal'
import { FaAngleDoubleUp, FaAngleDoubleDown, FaEquals } from "react-icons/fa"
import { AnimatePresence, motion } from "framer-motion"
import { api} from "../../../utils/variables"
import axios from "axios"
import Apaexlinecolumn from "../../../additional_components/common/charts-graphs/ApexLineChart"
import { Button, Card, CardBody, Col, Row, Spinner, Tooltip } from "reactstrap"
import { Info } from "react-feather"
import { CountryFlag } from "./FilterControls"
import { Search, Inbox, Plus, Minus } from "lucide-react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

const SortIcon = ({ sortKey, currentSort, currentDirection }) => {
  if (currentSort !== sortKey) {
    return (
      <span style={{
        marginLeft: '4px',
        color: '#ccc',
        fontSize: '12px'
      }}>
        ↕
      </span>
    )
  }

  return (
    <span style={{
      marginLeft: '4px',
      color: COLORS.primary,
      fontSize: '12px'
    }}>
      {currentDirection === 'asc' ? '↑' : '↓'}
    </span>
  )
}

const ProgressBar = ({ value }) => (
  <div className="progress-bar-wrapper">
    <div className="progress-bar">
      <div
        className="progress-fill"
        style={{ width: `${value}%` }}
      />
    </div>
    <div className="progress-label">{value}</div>
  </div>
)

const ChangeDisplay = ({ changeData }) => {
  if (!changeData || typeof changeData !== 'object')
    return <span style={{ color: '#6B7280' }}>No Change</span>

  const { text, direction } = changeData

  switch (direction) {
    case 'up':
      return (
        <span style={{ color: '#10B981', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FaAngleDoubleUp color="green" />
          <span>{text}</span>
        </span>
      )
    case 'down':
      return (
        <span style={{ color: '#EF4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FaAngleDoubleDown color="red" />
          <span>{text}</span>
        </span>
      )
    case 'none':
      return (
        <span style={{ color: '#F59E0B', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FaEquals color="yellow" />
          <span>{text}</span>
        </span>
      )
    default:
      return <span style={{ color: '#6B7280' }}>{text}</span>
  }
}

const KeywordTrackingTable = ({
  keywordsLoading,
  keywordTrackingData,
  item_id,
  countryName,
  awaitingLocaleSelection = false,
  planKeywordLimit = Infinity,
  isOverLimit = false,
  onUpgradeRequired = () => {}
}) => {
  const { trackEvent } = useAnalytics()

  const [showLiveRankingModal, setShowLiveRankingModal] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [liveRankingGames, setLiveRankingGames] = useState([]);
  const [liveRankingTotalApps, setLiveRankingTotalApps] = useState(0);
  const [liveRankingLoading, setLiveRankingLoading] = useState(false);

  // Defaults to Total descending so the keywords with the most ranked apps — the
  // most significant/competitive ones — show first, rather than in raw API order.
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });

  const [expandedRowIndex, setExpandedRowIndex] = useState(null)
  const [expandedKeyword, setExpandedKeyword] = useState(null)

  const [graphData, setGraphData] = useState([])
  const [localGraphData, setLocalGraphData] = useState([])
  const [graphLoader, setGraphLoader] = useState(false)

  const [checkedItems, setCheckedItems] = useState(["Rank", "Revenue", "Downloads"])
  const [selectedOption, setSelectedOption] = useState("Global")

  const [tooltipOpen, setTooltipOpen] = useState({})
  const [infoTooltip, setInfoTooltip] = useState(false)
  const [tright, settright] = useState(false);

  const [hadPreviousData, setHadPreviousData] = useState(false)
  const [isLoadingTransition, setIsLoadingTransition] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20

  const formatDate = d => new Date(d).toISOString().split("T")[0]
  const oneMonthAgo = () => {
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    return formatDate(d)
  }

  const [startDate, setStartDate] = useState(oneMonthAgo())
  const [endDate, setEndDate] = useState(formatDate(new Date()))
  const [searchKeyword, setSearchKeyword] = useState("")

  const toggleTooltip = key => {
    setTooltipOpen(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleLiveRankingClick = async (keyword) => {
    setSelectedKeyword(keyword)
    setShowLiveRankingModal(true)
    setLiveRankingLoading(true)
    setLiveRankingGames([])
    setLiveRankingTotalApps(0)
    try {
      const details = keywordTrackingData?.[keyword] || {}
      trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_LIVE_RANKING_VIEWED, {
        genre: keyword,
        country: details.country || countryName,
        language: details.language || "english",
      })
      const res = await axios.post(
        `${api}/tracker/tracking/category_live_ranking`,
        {
          category: keyword,
          game_id: item_id,
          country: details.country || countryName,
          language: details.language || "english",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      setLiveRankingGames(res.data?.data?.games || [])
      setLiveRankingTotalApps(res.data?.data?.total_apps || 0)
    } catch (error) {
      console.log(error)
    } finally {
      setLiveRankingLoading(false)
    }
  }

  const handleChange = (event) => {
    const selected = event.target.value;
    setSelectedOption(selected);
    applyFilter(selected);
  };

  const closeModal = () => {
    setShowLiveRankingModal(false)
    setSelectedKeyword('')
    setLiveRankingGames([])
    setLiveRankingTotalApps(0)
  }

  const handleSort = key => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const sortData = data => {
    if (!sortConfig.key) return data

    return [...data].sort((a, b) => {
      let aValue, bValue

      if (sortConfig.key === 'currentPosition') {
        aValue = a.currRaw
        bValue = b.currRaw
      } else if (sortConfig.key === 'lastPosition') {
        aValue = a.lastRaw
        bValue = b.lastRaw
      } else if (sortConfig.key === 'total') {
        aValue = a.totalApps
        bValue = b.totalApps
      } else return 0

      if (sortConfig.key === 'total') {
        // Missing totals sort as 0 (lowest), not "worst position" (MAX_SAFE_INTEGER) —
        // there's no ranking-quality semantic here, just an app count.
        if (aValue == null) aValue = 0
        if (bValue == null) bValue = 0
      } else {
        if (aValue == null) aValue = Number.MAX_SAFE_INTEGER
        if (bValue == null) bValue = Number.MAX_SAFE_INTEGER
      }

      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue
    })
  }

  const toggleExpandRow = (globalIndex, keyword) => {
    if (expandedRowIndex === globalIndex) {
      setExpandedRowIndex(null)
      setExpandedKeyword(null)
    } else {
      setExpandedRowIndex(globalIndex)
      setExpandedKeyword(keyword)
      setCheckedItems(["Rank", "Revenue", "Downloads"])

      const details = keywordTrackingData?.[keyword] || {}
      trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_GENRE_VIEWED, {
        genre: keyword,
        country: details.country || countryName,
        language: details.language || "english",
      })
    }
  }

  const getGraphData = async (keyword, gameId) => {
    setGraphLoader(true)
    try {
      const res = await axios.post(
        `${api}/tracker/tracking/keyword_details`,
        {
          type: "category",
          entity_id: keyword,
          game_id: gameId,
          start_date: startDate,
          end_date: endDate
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          }
        }
      )
      setGraphData(res.data.data.rankings || [])
    } finally {
      setGraphLoader(false)
    }
  }

  const getLocalGraph = async gameId => {
    setGraphLoader(true)
    try {
      const res = await axios.post(
        `${api}/tracker/tracking/app_graph`,
        {
          type: "keyword",
          item_id: gameId,
          start_date: startDate,
          end_date: endDate
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          }
        }
      )
      setLocalGraphData(res.data.history || [])
    } finally {
      setGraphLoader(false)
    }
  }
  useEffect(() => {
    if (expandedKeyword) {
      if (selectedOption === "Local") getLocalGraph(item_id);
      else getGraphData(expandedKeyword, item_id);
    }
  }, [startDate, endDate]);

  // Track if we previously had data to prevent flash of no data during refresh
  useEffect(() => {
    if (keywordTrackingData) {
      const keys = Object.keys(keywordTrackingData).filter(k => k !== "game_data")
      if (keys.length > 0) {
        setHadPreviousData(true)
        setIsLoadingTransition(false)
      }
    }
  }, [keywordTrackingData])

  // Track loading transitions with timeout to prevent flash
  useEffect(() => {
    if (keywordsLoading && hadPreviousData) {
      setIsLoadingTransition(true)
    } else if (!keywordsLoading && keywordTrackingData) {
      const timer = setTimeout(() => {
        setIsLoadingTransition(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [keywordsLoading, hadPreviousData, keywordTrackingData])


  // useEffect(() => {
  //   if (!keywordTrackingData || keywordsLoading) return;

  //   const entries = Object.entries(keywordTrackingData || {}).filter(([k]) => k !== "game_data")
  //   if (entries.length === 0) return

  //   const firstKeyword = entries[0][0]

  //   setExpandedRowIndex(0)
  //   setExpandedKeyword(firstKeyword)
  //   getGraphData(firstKeyword, item_id)

  // }, [keywordTrackingData, keywordsLoading])


  const applyFilter = selected => {
    if (selected === "Local") getLocalGraph(item_id)
    else getGraphData(expandedKeyword, item_id)
  }


  const handleCheckboxChange = (e, name) => {
    if (e.target.checked) {
      setCheckedItems(prev => [...prev, name])
    } else {
      setCheckedItems(prev => prev.filter(i => i !== name))
    }
  }

  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const renderTableHeader = () => (
    <thead>
      <tr>
        {TABLE_COLUMNS.map(col => {
          const tooltipId = `th-tooltip-${col.key}`

          // Special handling for keyword column
          if (col.key === 'keyword') {
            return (
              <th key={col.key} style={{ textAlign: "left" }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                  {!isSearchOpen && (
                    <>
                      {/* Search Icon */}
                      <Search
                        size={16}
                        color="#F6255A"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsSearchOpen(true)}
                      />
                      <span style={{ fontWeight: 'bold', color: '#FFFFFF' }}>{col.label}</span>
                    </>
                  )}
                  {isSearchOpen && (
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search keyword..."
                      value={searchKeyword}
                      onChange={e => {
                        setSearchKeyword(e.target.value)
                        setCurrentPage(1) // reset pagination on search
                      }}
                      onBlur={() => setIsSearchOpen(false)}
                      style={{
                        width: "180px",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: "1px solid #3a3d4a",
                        backgroundColor: "#1e2028",
                        color: "#FFFFFF",
                        outline: "none",
                      }}
                    />
                  )}
                </div>
              </th>
            )
          }

          // Default rendering for other columns
          return (
            <th key={col.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: col.sortable ? 'pointer' : 'default' }}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                {col.label}
                <span id={tooltipId}><Info size={14} color="#9ca3af" /></span>
                <Tooltip
                  isOpen={tooltipOpen[col.key]}
                  target={tooltipId}
                  toggle={() => toggleTooltip(col.key)}
                >
                  {col.tooltip}
                </Tooltip>
                {col.sortable && (
                  <SortIcon
                    sortKey={col.key}
                    currentSort={sortConfig.key}
                    currentDirection={sortConfig.direction}
                  />
                )}
              </div>
            </th>
          )
        })}
      </tr>
    </thead>
  )


  const renderTableBody = () => {

    const hasData = keywordTrackingData && Object.keys(keywordTrackingData).filter(k => k !== "game_data").length > 0
    const shouldShowSkeleton = keywordsLoading || !keywordTrackingData || isLoadingTransition

    if (awaitingLocaleSelection) {
      return (
        <tbody>
          <tr>
            <td colSpan="6">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  color: "#9CA3AF",
                }}
              >
                <div
                  style={{
                    background: "#2C2D33",
                    borderRadius: "50%",
                    padding: "20px",
                    marginBottom: "16px",
                  }}
                >
                  <Inbox size={40} color="#4B5563" />
                </div>
                <h5 style={{ color: "#E5E7EB", marginBottom: "8px" }}>Select a Country &amp; Language</h5>
                <p style={{ fontSize: "14px", margin: 0 }}>
                  Choose a country and language above to start tracking genres for this app.
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    if (shouldShowSkeleton) {
      return (
        <tbody>
          {[...Array(5)].map((_, index) => (
            <tr key={`skeleton-${index}`} style={{ borderBottom: "1px solid #3a3d4a" }}>
              {TABLE_COLUMNS.map((col, colIndex) => (
                <td key={colIndex} style={{ padding: "12px 8px" }}>
                  <Skeleton baseColor="#2C2D33" highlightColor="#3A3C46" height={20} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      );
    }

    const entries = Object.entries(keywordTrackingData || {}).filter(([k]) => k !== "game_data");

    if (entries.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan="6">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  color: "#9CA3AF",
                }}
              >
                <div
                  style={{
                    background: "#2C2D33",
                    borderRadius: "50%",
                    padding: "20px",
                    marginBottom: "16px",
                  }}
                >
                  <Inbox size={40} color="#4B5563" />
                </div>
                <h5 style={{ color: "#E5E7EB", marginBottom: "8px" }}>No Data Available</h5>
                <p style={{ fontSize: "14px", margin: 0 }}>
                  There are no keywords tracked for this selection yet.
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }


    // Create dataset
    const dataEntries = Object.entries(keywordTrackingData || {})
      .filter(([key]) => key !== "game_data")

    const generateStablePercentage = (seed) => {
      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }
      return (Math.abs(hash) % 100) + 1;
    };

    // Create dataset
    const tableData = dataEntries.map(([keyword, details]) => {
      const loading = !!details._loading || !details.has_data

      // The backend computes current_rank/last_position/total_apps/change
      // directly now (see build_change_state in tracker/tracking/app/service.py).
      const currRaw = details.current_rank
      const lastRaw = details.last_position
      const totalApps = details.total_apps || 0

      const currentPosition = formatPosition(currRaw, totalApps);
      const lastPosition = formatPosition(lastRaw, totalApps);

      return {
        keyword,
        loading,
        currentPosition,
        lastPosition,
        changeData: details.change,
        currRaw,
        lastRaw,
        popularity: generateStablePercentage(keyword + "_popularity"),
        competitiveness: generateStablePercentage(keyword + "_competitiveness"),
        totalApps
      };
    });

    const filteredData = tableData.filter((row) =>
      row.keyword.toLowerCase().includes(searchKeyword.toLowerCase())
    );

    if (filteredData.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan="6">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 20px",
                  color: "#9CA3AF",
                }}
              >
                <Search size={32} color="#4B5563" style={{ marginBottom: "12px" }} />
                <p style={{ fontSize: "14px", margin: 0 }}>
                  No keywords found matching "{searchKeyword}"
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    // Sort filtered data
    const sortedData = sortData(filteredData);

    // Pagination
    const totalItems = sortedData.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const start = (currentPage - 1) * pageSize;
    const currentItems = sortedData.slice(start, start + pageSize);


    return (
      <tbody>
        {currentItems.map((row, idx) => {
          const globalIndex = start + idx
          return (
            <React.Fragment key={row.keyword}>
              {/* MAIN ROW */}
              <tr
                style={{ cursor: 'pointer', marginBottom: '8px', borderBottom: '1px solid #3a3d4a' }}
              >
                <td
                  style={{
                    textAlign: 'left',
                    padding: '12px 8px',
                    cursor: 'pointer',
                    backgroundColor: '#2C2D33',
                    color: '#FFFFFF'
                  }}
                  onClick={() => toggleExpandRow(globalIndex, row.keyword)}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <CountryFlag
                      countryName={countryName}
                      style={{ marginRight: "4px", flexShrink: 0 }}
                    />
                    <div className="keyword-text-cell">
                      <strong>{row.keyword}</strong>
                    </div>

                    {/* Expand / Collapse Icon */}
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                        borderRadius: "4px",
                        background: expandedRowIndex === globalIndex ? "#F6255A" : "#3A3D4A",
                        color: "#FFFFFF",
                        fontSize: "12px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {expandedRowIndex === globalIndex ? (
                        <Minus size={12} />
                      ) : (
                        <Plus size={12} />
                      )}
                    </span>
                  </div>
                </td>
                <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                  {row.loading ? 'Loading...' : row.currentPosition}
                </td>
                <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                  {row.loading ? 'Loading...' : row.lastPosition}
                </td>
                <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                  {row.loading ? <span style={{ color: '#9CA3AF' }}>Loading…</span> : <ChangeDisplay changeData={row.changeData} />}
                </td>
                {/* <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                  <ProgressBar value={row.popularity} />
                </td>
                <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                  <ProgressBar value={row.competitiveness} />
                </td> */}
                <td style={{ textAlign: "center", padding: "12px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", width: "100%" }}>
                    <button
                      type="button"
                      onClick={() => handleLiveRankingClick(row.keyword)}
                      disabled={row.loading}
                      style={{
                        background: 'linear-gradient(135deg, #5A5A5A 0%, #C49598 100%)',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: row.loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(128, 128, 128, 0.3)',
                        opacity: row.loading ? 0.7 : 1,
                        whiteSpace: "nowrap"
                      }}
                      onMouseEnter={(e) => {
                        if (!row.loading) {
                          e.target.style.background = 'linear-gradient(135deg, #4A4A4A 0%, #B08588 100%)';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(128, 128, 128, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #5A5A5A 0%, #C49598 100%)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(128, 128, 128, 0.3)';
                      }}
                    >
                      {row.loading ? 'Fetching…' : 'View'}
                    </button>
                  </div>
                </td>
                <td style={{ textAlign: "center", padding: "12px 8px", color: "#FFFFFF", fontWeight: "bold" }}>
                  {row.loading ? '…' : row.totalApps}
                </td>
              </tr>

              {/* EXPANDED ROW */}
              <AnimatePresence>
                {expandedRowIndex === globalIndex && (
                  <tr>
                    <td colSpan="6">
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{
                          height: 0,
                          opacity: 0,
                          transition: { duration: 0.4, ease: "easeInOut" },
                        }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        style={{
                          padding: "0",
                          margin: "0",
                          backgroundColor: "transparent",
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: "0",
                          overflow: "visible",
                          boxShadow: "none",
                        }}
                      >
                        <div style={{
                          padding: "20px",
                          backgroundColor: "#23272b",
                          border: "1px solid #3a3d4a",
                          borderRadius: "10px",
                          margin: "18px 10px 10px 10px",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
                          overflow: 'hidden'
                        }}>
                          <div style={{ marginBottom: "20px" }}>
                            <h6
                              style={{
                                color: "#FFFFFF",
                                marginBottom: "10px",
                                fontSize: "16px",
                                wordBreak: "break-all",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              }}
                            >
                              Keyword: <strong>{expandedKeyword}</strong>
                            </h6>
                          </div>

                          <div style={{ marginBottom: "20px" }}>
                            <div className="d-flex flex-wrap justify-content-between align-items-center">
                              <div className="d-flex flex-wrap gap-4 align-items-center">
                                <label
                                  htmlFor="start-date-input"
                                  style={{ color: "#FFFFFF", marginBottom: "0", fontSize: "14px" }}
                                >
                                  From
                                </label>
                                <div>
                                  <input
                                    className="form-control"
                                    type="date"
                                    defaultValue={startDate}
                                    max={new Date().toISOString().split("T")[0]}
                                    onChange={e => {
                                      setStartDate(e.target.value)
                                    }}
                                    id="start-date-input"
                                    style={{
                                      backgroundColor: "#1e2028",
                                      border: "1px solid #3a3d4a",
                                      color: "#FFFFFF",
                                      fontSize: "14px",
                                      padding: "8px 12px"
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="d-flex flex-wrap gap-4 align-items-center">
                                <label
                                  htmlFor="end-date-input"
                                  style={{ color: "#FFFFFF", marginBottom: "0", fontSize: "14px" }}
                                >
                                  To
                                </label>
                                <div>
                                  <input
                                    className="form-control"
                                    type="date"
                                    defaultValue={endDate}
                                    max={new Date().toISOString().split("T")[0]}
                                    onChange={e => {
                                      setEndDate(e.target.value)
                                    }}
                                    id="end-date-input"
                                    style={{
                                      backgroundColor: "#1e2028",
                                      border: "1px solid #3a3d4a",
                                      color: "#FFFFFF",
                                      fontSize: "14px",
                                      padding: "8px 12px"
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-md-3">
                                <select
                                  className="form-control"
                                  onChange={handleChange}
                                  style={{
                                    backgroundColor: "#1e2028",
                                    border: "1px solid #3a3d4a",
                                    color: "#FFFFFF",
                                    fontSize: "14px",
                                    padding: "8px 12px"
                                  }}
                                >
                                  <option value="Global">
                                    Global Revenue/Downloads
                                  </option>
                                  <option value="Local">
                                    App Revenue/Downloads
                                  </option>
                                </select>
                              </div>
                              <Button
                                color="primary"
                                onClick={() => applyFilter(selectedOption)}
                                style={{
                                  background: "linear-gradient(135deg, #F6255A 0%, #F6255A 100%)",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "8px 16px",
                                  fontWeight: "600",
                                  fontSize: "14px"
                                }}
                              >
                                Apply Range
                              </Button>
                            </div>
                          </div>

                          {graphLoader ? (
                            <div className="d-flex justify-content-center" style={{ padding: "20px" }}>
                              <Spinner></Spinner>
                            </div>
                          ) : (
                            <>
                              <div style={{ marginBottom: "20px" }} className="d-flex justify-content-start gap-5">
                                <div className="d-flex flex-wrap justify-content-start gap-3">
                                  <label style={{ color: "#FFFFFF", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                                    <input
                                      type="checkbox"
                                      defaultChecked="true"
                                      onChange={e =>
                                        handleCheckboxChange(e, "Rank")
                                      }
                                      style={{ accentColor: "#F6255A" }}
                                    />
                                    Rank
                                  </label>
                                  <label style={{ color: "#FFFFFF", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                                    <input
                                      type="checkbox"
                                      defaultChecked="true"
                                      onChange={e =>
                                        handleCheckboxChange(e, "Revenue")
                                      }
                                      style={{ accentColor: "#F6255A" }}
                                    />
                                    Revenue
                                  </label>
                                  <label style={{ color: "#FFFFFF", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                                    <input
                                      type="checkbox"
                                      defaultChecked="true"
                                      onChange={e =>
                                        handleCheckboxChange(e, "Downloads")
                                      }
                                      style={{ accentColor: "#F6255A" }}
                                    />
                                    Downloads
                                  </label>
                                </div>
                                <Tooltip
                                  placement="right"
                                  isOpen={tright}
                                  target="TooltipRight"
                                  toggle={() => {
                                    settright(!tright)
                                  }}
                                >
                                  Click to learn more about graph trends
                                </Tooltip>
                                <Info
                                  id="TooltipRight"
                                  size={20}
                                  onClick={() => {
                                    settright(!tright)
                                  }}
                                  style={{ cursor: "pointer", color: "#FFFFFF" }}
                                />
                              </div>
                              <div style={{ backgroundColor: "#2C2D33", padding: "20px", borderRadius: "8px" }}>
                                <Apaexlinecolumn
                                  data={graphData}
                                  selectedData={selectedOption}
                                  localData={localGraphData}
                                  labels={checkedItems}
                                />
                              </div>
                            </>
                          )}
                        </div>

                      </motion.div>
                    </td>
                  </tr>
                )}
              </AnimatePresence>
            </React.Fragment>
          )
        })}
      </tbody>
    )
  }


  const totalItems = Object.keys(keywordTrackingData || {}).filter(k => k !== "game_data").length
  const totalPages = Math.ceil(totalItems / pageSize)

  return (
    <>
      <div className="track_table_main">

        <div className="track_table">
          <table className="table">
            {renderTableHeader()}
            {renderTableBody()}
          </table>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
            gap: '10px'
          }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              style={{
                padding: "6px 12px",
                backgroundColor: "#3a3d4a",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer"
              }}
            >
              Previous
            </button>

            {[...Array(totalPages).keys()].map(num => {
              const page = num + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: page === currentPage ? "#F6255A" : "#2C2D33",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  {page}
                </button>
              );
            })}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              style={{
                padding: "6px 12px",
                backgroundColor: "#3a3d4a",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer"
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <LiveRankingModal
        show={showLiveRankingModal}
        handleClose={closeModal}
        games={liveRankingGames}
        totalApps={liveRankingTotalApps}
        loading={liveRankingLoading}
        keyword={selectedKeyword}
      />

    </>
  )
}

export default KeywordTrackingTable
